import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { WhatsappLogo, PaperPlaneTilt, FileText, Receipt, IdentificationCard, Image as ImageIcon, Sparkle, ChatCircle, Copy, ArrowClockwise, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const SCENARIOS = [
  { id: "bill_ready", label: "Bill is ready" },
  { id: "doc_ready",  label: "Document ready for collection" },
  { id: "reminder",   label: "Payment / document reminder" },
  { id: "greeting",   label: "Warm greeting reply" },
  { id: "thanks",     label: "Thank you note" },
  { id: "followup",   label: "Follow-up on pending status" },
  { id: "custom",     label: "Custom · use context below" },
];

const LANGUAGES = ["English", "Marathi", "Hindi", "Marathi + English", "Hindi + English"];

const KIND_META = {
  message:   { label: "Message",  icon: ChatCircle,        tone: "bg-slate-100 text-slate-800" },
  document:  { label: "Document", icon: FileText,          tone: "bg-cyan-50 text-cyan-800" },
  bill:      { label: "Bill",     icon: Receipt,           tone: "bg-emerald-50 text-emerald-800" },
  agreement: { label: "Agreement",icon: FileText,          tone: "bg-blue-50 text-blue-800" },
  photo:     { label: "Photo",    icon: ImageIcon,         tone: "bg-purple-50 text-purple-800" },
  ai_reply:  { label: "AI Reply", icon: Sparkle,           tone: "bg-fuchsia-50 text-fuchsia-800" },
};

export default function WhatsAppCenter() {
  const [dash, setDash] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [openSend, setOpenSend] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const load = async () => {
    try {
      const [{ data: d }, { data: c }] = await Promise.all([
        api.get("/whatsapp/outbound/dashboard"),
        api.get("/customers"),
      ]);
      setDash(d);
      setCustomers(c);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="max-w-[1500px] mx-auto p-6" data-testid="wa-center-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <WhatsappLogo size={16} weight="fill" className="text-emerald-500" />
          <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">WhatsApp Business Center</span>
        </div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Business Communication</h1>
        <p className="text-sm text-slate-500 mt-1">Deep-link launcher · Today's outbound stats · AI Reply Assistant · <span className="text-emerald-700">Respects WhatsApp ToS — no QR bridging, no auto-send.</span></p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="wa-stat-cards">
        <BigStat label="Outbound today" value={dash?.outbound_today ?? 0} icon={PaperPlaneTilt} tone="emerald" testid="wa-stat-outbound" />
        <BigStat label="Docs shared today" value={dash?.docs_shared_today ?? 0} icon={FileText} tone="cyan" testid="wa-stat-docs" />
        <BigStat label="Contacts reached" value={dash?.contacts_reached_today ?? 0} icon={ChatCircle} tone="blue" testid="wa-stat-contacts" />
        <BigStat label="AI replies used" value={dash?.by_kind?.ai_reply ?? 0} icon={Sparkle} tone="fuchsia" testid="wa-stat-ai" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick actions */}
        <Card className="p-5 bg-white border-slate-200 lg:col-span-1" data-testid="wa-quick-actions">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Quick Actions</div>
          <div className="grid grid-cols-1 gap-2">
            <QuickAction icon={WhatsappLogo} tone="emerald" label="Open WhatsApp Web" onClick={() => window.open("https://web.whatsapp.com/", "_blank", "noopener,noreferrer")} testid="wa-open-web" />
            <QuickAction icon={PaperPlaneTilt} tone="orange" label="Send Message / Doc / Bill" onClick={() => setOpenSend(true)} testid="wa-send-open" />
            <QuickAction icon={Sparkle} tone="fuchsia" label="AI Reply Assistant" onClick={() => setAiOpen(true)} testid="wa-ai-open" />
            <QuickAction icon={ArrowClockwise} tone="slate" label="Refresh Stats" onClick={load} testid="wa-refresh" />
          </div>

          <div className="mt-6 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">By Type Today</div>
          {dash && Object.keys(dash.by_kind || {}).length === 0 ? (
            <div className="text-xs text-slate-400 py-4 text-center">No messages logged yet.</div>
          ) : (
            <div className="space-y-1.5">
              {Object.entries(dash?.by_kind || {}).map(([k, count]) => {
                const meta = KIND_META[k] || KIND_META.message;
                return (
                  <div key={k} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5"><meta.icon size={13} /> {meta.label}</span>
                    <Badge variant="outline" className={`text-[10px] ${meta.tone}`}>{count}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Recent activity feed */}
        <Card className="p-5 bg-white border-slate-200 lg:col-span-2" data-testid="wa-recent-feed">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 flex items-center gap-1.5"><ClockCounterClockwise size={11} /> Live Activity Feed</div>
          </div>
          {(!dash?.recent || dash.recent.length === 0) ? (
            <div className="text-center py-16 text-slate-400 text-sm">
              No outbound activity yet. Every wa.me link opened from the ERP will appear here.
            </div>
          ) : (
            <div className="space-y-3 max-h-[520px] overflow-y-auto">
              {dash.recent.map((e) => {
                const meta = KIND_META[e.kind] || KIND_META.message;
                return (
                  <div key={e.id} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0" data-testid={`wa-event-${e.id}`}>
                    <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${meta.tone}`}>
                      <meta.icon size={16} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 font-medium">
                        {e.customer_name || e.mobile || "Unknown"} <span className="text-slate-400 font-normal">·</span> <span className="text-xs text-slate-500">{meta.label}</span>
                      </div>
                      {e.subject && <div className="text-xs text-slate-500 truncate">{e.subject}</div>}
                      {e.attachment_name && <div className="text-[11px] text-slate-400 truncate">📎 {e.attachment_name}</div>}
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        {e.at ? new Date(e.at).toLocaleString("en-IN", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""} · {e.operator}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <SendDialog open={openSend} onOpenChange={setOpenSend} customers={customers} onSent={load} />
      <AIReplyDialog open={aiOpen} onOpenChange={setAiOpen} customers={customers} onLog={load} />
    </div>
  );
}

function QuickAction({ icon: Icon, tone, label, onClick, testid }) {
  const t = {
    orange:  "bg-orange-600 text-white",
    emerald: "bg-emerald-600 text-white",
    fuchsia: "bg-fuchsia-600 text-white",
    slate:   "bg-slate-100 text-slate-800",
  }[tone];
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium ${t} hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow] duration-200 text-left`}>
      <Icon size={18} weight="fill" />
      {label}
    </button>
  );
}

function BigStat({ label, value, icon: Icon, tone = "orange", testid }) {
  const toneMap = {
    orange:  "text-orange-700 bg-orange-50 border-orange-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    cyan:    "text-cyan-700 bg-cyan-50 border-cyan-200",
    blue:    "text-blue-700 bg-blue-50 border-blue-200",
    fuchsia: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200",
  };
  return (
    <Card className="p-4 bg-white border-slate-200" data-testid={testid}>
      <div className={`w-9 h-9 rounded-md border flex items-center justify-center ${toneMap[tone]} mb-2`}>
        <Icon size={17} weight="duotone" />
      </div>
      <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
    </Card>
  );
}

// ============ Send dialog — wa.me deeplink builder ============
function SendDialog({ open, onOpenChange, customers, onSent }) {
  const [customerId, setCustomerId] = useState("_none");
  const [mobile, setMobile] = useState("");
  const [kind, setKind] = useState("message");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachment, setAttachment] = useState("");

  useEffect(() => {
    if (!open) { setCustomerId("_none"); setMobile(""); setKind("message"); setSubject(""); setBody(""); setAttachment(""); }
  }, [open]);

  const customer = customers.find((c) => c.id === customerId);
  useEffect(() => { if (customer?.mobile) setMobile(customer.mobile); }, [customer]);

  const send = async () => {
    const num = (mobile || "").replace(/\D/g, "");
    if (num.length < 10) { toast.error("Enter a valid mobile (10 digits)"); return; }
    const finalNum = num.length === 10 ? `91${num}` : num;
    const preview = subject ? `${subject}\n\n${body}` : body;
    const text = encodeURIComponent(preview || `Hello from DigitalSetu`);
    const url = `https://wa.me/${finalNum}?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
    // Log the outbound event
    try {
      await api.post("/whatsapp/outbound/log", {
        mobile: finalNum,
        customer_id: customerId === "_none" ? "" : customerId,
        customer_name: customer?.name || "",
        kind, subject, body: preview, attachment_name: attachment,
      });
      toast.success("WhatsApp opened · event logged");
      onSent?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Log failed (but WhatsApp opened)"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="wa-send-dialog">
        <DialogHeader><DialogTitle>Send via WhatsApp</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>Customer</Label>
            <Select value={customerId} onValueChange={setCustomerId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="_none">— Enter mobile manually —</SelectItem>
                {customers.slice(0, 300).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.mobile}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Mobile (with country code optional)</Label><Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="9876543210" data-testid="wa-send-mobile" /></div>
          <div>
            <Label>Kind</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="wa-send-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(KIND_META).filter(([k]) => k !== "ai_reply").map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Subject (optional)</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Your bill is ready" /></div>
          <div><Label>Message body</Label><Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your message here…" data-testid="wa-send-body" /></div>
          {kind !== "message" && (
            <div>
              <Label>Attachment name (log-only)</Label>
              <Input value={attachment} onChange={(e) => setAttachment(e.target.value)} placeholder="e.g. Invoice-INV0125.pdf · The file itself must be attached inside WhatsApp Web" />
              <div className="text-[10px] text-slate-500 mt-1 italic">WhatsApp doesn't allow direct file attachment via wa.me. The ERP will open the chat — attach the file in WhatsApp Web/App.</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="wa-send-btn"><WhatsappLogo size={14} weight="fill" /> Open Chat & Log</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ AI Reply Assistant ============
function AIReplyDialog({ open, onOpenChange, customers, onLog }) {
  const [customerId, setCustomerId] = useState("_none");
  const [scenario, setScenario] = useState("bill_ready");
  const [language, setLanguage] = useState("English");
  const [context, setContext] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy] = useState(false);

  const customer = customers.find((c) => c.id === customerId);

  const generate = async () => {
    setBusy(true); setDrafts([]);
    try {
      const { data } = await api.post("/whatsapp/ai-reply", {
        scenario, language,
        customer_name: customer?.name || "",
        context: context || "",
      });
      setDrafts(data.drafts || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI failed"); }
    finally { setBusy(false); }
  };

  const sendDraft = async (text) => {
    if (!customer?.mobile) {
      navigator.clipboard.writeText(text);
      toast.success("Copied — paste into WhatsApp");
      return;
    }
    const num = (customer.mobile || "").replace(/\D/g, "");
    const finalNum = num.length === 10 ? `91${num}` : num;
    const url = `https://wa.me/${finalNum}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    try {
      await api.post("/whatsapp/outbound/log", {
        mobile: finalNum, customer_id: customerId, customer_name: customer.name,
        kind: "ai_reply", subject: SCENARIOS.find((s) => s.id === scenario)?.label || scenario, body: text,
      });
      onLog?.();
      toast.success("Chat opened · AI reply logged");
    } catch {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl" data-testid="wa-ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkle size={18} weight="fill" className="text-fuchsia-500" /> AI Reply Assistant</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Customer</Label>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="_none">— None (copy to clipboard) —</SelectItem>
                  {customers.slice(0, 300).map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.mobile}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="wa-ai-language"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Scenario</Label>
            <Select value={scenario} onValueChange={setScenario}>
              <SelectTrigger data-testid="wa-ai-scenario"><SelectValue /></SelectTrigger>
              <SelectContent>{SCENARIOS.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {scenario === "custom" && (
            <div><Label>Context</Label><Textarea rows={3} value={context} onChange={(e) => setContext(e.target.value)} placeholder="Describe what to convey…" /></div>
          )}
          <Button onClick={generate} disabled={busy} className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white gap-2" data-testid="wa-ai-generate">
            <Sparkle size={14} weight="fill" /> {busy ? "AI thinking…" : "Generate 3 replies"}
          </Button>
          {drafts.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Suggestions</div>
              {drafts.map((d, i) => (
                <Card key={i} className="p-3 bg-slate-50 border-slate-200" data-testid={`wa-ai-draft-${i}`}>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">{d}</div>
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(d); toast.success("Copied"); }} className="gap-1 text-xs"><Copy size={11} /> Copy</Button>
                    <Button size="sm" onClick={() => sendDraft(d)} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1 text-xs" data-testid={`wa-ai-send-${i}`}>
                      <WhatsappLogo size={11} weight="fill" /> Send via WhatsApp
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
